/* eslint-disable no-unused-vars */
import type {
  AchAccountType,
  BankAccountAttributes,
  BankAccountType,
  InvestmentAccountAttributes,
  InvestmentAccountType,
  LinkedAccountStatus,
  Money,
  RetirementContribution,
} from '@egw/api'
import type { TFunction } from '@egw/i18n'
import { type UseFormReturn, type UseFormSetError } from 'react-hook-form'

// These error types are not shown to the user as messages, only as error state on the input
export const HIDDEN_ERROR_MESSAGES = [
  'required',
  'nullable',
  'length',
  'email',
  'testUSPhoneNumber',
  'testDateOfBirth',
  'testSSN',
]

export enum TransferField {
  To = 'to',
  From = 'from',
  Amount = 'amount',
}

export type TransferAccounts = BankAccountType | InvestmentAccountType | AchAccountType

export interface TransferListOption {
  value: string
  display: {
    name: string
    subtitle: string
    isEvergreenAccount: boolean
    // field for Null Transfer Option
    isHidden?: boolean
    // Leave in display because transfer v1 is looking for this here
    linkedAccountStatus?: LinkedAccountStatus
    // Leave in display because transfer v1 is looking for this here
    achType?: AchAccountType
  }
  accountType: TransferAccounts
  accountTitle?: string
  alias: number
  transferableToAccounts: TransferAccounts[]
  isDisabled: boolean
  attributes?: BankAccountAttributes | InvestmentAccountAttributes | undefined
  retirementContribution?: RetirementContribution
}

export interface ListBoxOption {
  value: string | number | null
  display: Record<string, string | boolean | number | undefined>
}

export enum EModalType {
  From = 'from',
  To = 'to',
}

export interface TSelectOption {
  option: TransferListOption
  key: TransferField.From | TransferField.To
}

export interface TResetFieldOption {
  key: TransferField
}

export type TFormMethods = UseFormReturn<{
  [x: string]: any
  [x: number]: any
  [x: symbol]: any
}>

export interface AddCentsInterface {
  formMethods: UseFormReturn<TransferFormInterface>
}

export interface TOptionsModal {
  modalType: EModalType
  options: ListBoxOption[]
  selectOptionCb: (option: TransferListOption) => void
}

export interface TBuildPayload {
  fromPosition: TransferListOption
  toPosition: TransferListOption
  amount: string
  liquidateAccount?: boolean
}

export interface TBuildInvestmentAchPayload {
  fromPosition: TransferListOption
  toPosition: TransferListOption
  amount: string
}
export interface TSetMinAmountError {
  t: TFunction
  setError: UseFormSetError<TransferFormInterface>
  money: Money
}

export enum ETransferType {
  ACH = 'ACH',
  BankInvestment = 'BANK_INVESTMENT',
  BookTransfer = 'BOOK_TRANSFER',
  InvestmentAch = 'INVESTMENT_ACH',
  NA = 'NOT_AVAILABLE',
}

export interface TGetDisplayValueFromField {
  fieldOption: ListBoxOption | undefined
  allFieldOptions: ListBoxOption[]
  isModalOpen: boolean
  t: TFunction<'features-transfer'>
}

export interface TransferMutationPayloadInterface {
  [TransferField.From]: TransferListOption
  [TransferField.To]: TransferListOption
  [TransferField.Amount]: string
}

export enum BankInvestmentTransferType {
  Deposit = 'DEPOSIT',
  Withdraw = 'WITHDRAW',
}

export enum TransferSuccessTypes {
  DepositMarketClosed = 'DEPOSIT_MARKET_CLOSED',
  DepositMarketOpen = 'DEPOSIT_MARKET_OPEN',
  WithdrawMarketClosed = 'WITHDRAW_MARKET_CLOSED',
  WithdrawMarketOpen = 'WITHDRAW_MARKET_OPEN',
  Ach = 'ACH',
  InternalBankTransfer = 'INTERNAL_BANK_TRANSFER',
  InvestmentAch = 'INVESTMENT_ACH',
}

export enum WireTransferField {
  id = 'id',
  from = 'from',
  amount = 'amount',
  recipientInfo = 'recipientInfo',
  reference = 'reference',
  beneficiaryRoutingNumber = 'beneficiaryRoutingNumber',
  beneficiaryAccountNumber = 'beneficiaryAccountNumber',
  beneficiaryVerifyAccountNumber = 'beneficiaryVerifyAccountNumber',
  businessName = 'businessName',
  beneficiaryFirstName = 'beneficiaryFirstName',
  beneficiaryLastName = 'beneficiaryLastName',
  line1 = 'line1',
  city = 'city',
  region = 'region',
  postalCode = 'postalCode',
  country = 'country',
  recipientIsBusiness = 'recipientIsBusiness',
  hasIntermediaryBank = 'hasIntermediaryBank',
  intermediaryRoutingNumber = 'intermediaryRoutingNumber',
}

export enum InvestmentWireTransferField {
  id = 'id',
  from = 'from',
  amount = 'amount',
  recipientInfo = 'recipientInfo',
  // TODO: Add reference back in when we have it
  // reference = 'reference', // memo
  beneficiaryRoutingNumber = 'beneficiaryRoutingNumber',
  beneficiaryAccountNumber = 'beneficiaryAccountNumber',
  beneficiaryVerifyAccountNumber = 'beneficiaryVerifyAccountNumber',
  beneficiaryAccountTitle = 'beneficiaryAccountTitle',
  line1 = 'line1',
  city = 'city',
  region = 'region',
  postalCode = 'postalCode',
  country = 'country',
}

export interface WireTransferFormInterface {
  [WireTransferField.id]: string | undefined
  [WireTransferField.from]: TransferListOption
  [WireTransferField.amount]: string | undefined
  [WireTransferField.recipientInfo]: string
  [WireTransferField.reference]: string
  [WireTransferField.beneficiaryRoutingNumber]: string
  [WireTransferField.beneficiaryAccountNumber]: string
  [WireTransferField.beneficiaryVerifyAccountNumber]: string
  [WireTransferField.businessName]: string
  [WireTransferField.beneficiaryFirstName]: string
  [WireTransferField.beneficiaryLastName]: string
  [WireTransferField.line1]: string
  [WireTransferField.city]: string
  [WireTransferField.region]: string
  [WireTransferField.postalCode]: string
  [WireTransferField.country]: string
  [WireTransferField.recipientIsBusiness]: boolean
  [WireTransferField.hasIntermediaryBank]: boolean
  [WireTransferField.intermediaryRoutingNumber]: string
}

export interface RecipientInformFormInterface {
  [WireTransferField.beneficiaryRoutingNumber]?: string
  [WireTransferField.beneficiaryAccountNumber]?: string
  [WireTransferField.beneficiaryVerifyAccountNumber]?: string
  [WireTransferField.beneficiaryFirstName]?: string
  [WireTransferField.beneficiaryLastName]?: string
  [WireTransferField.beneficiaryFirstName]?: string
  [WireTransferField.beneficiaryLastName]?: string
  [WireTransferField.businessName]?: string
  [WireTransferField.line1]?: string
  [WireTransferField.region]?: string
  [WireTransferField.postalCode]?: string
  [WireTransferField.city]?: string
  [WireTransferField.postalCode]?: string
  [WireTransferField.intermediaryRoutingNumber]?: string
}

export interface InvestmentWireTransferFormInterface extends InvestmentRecipientInfoFormInterface {
  [InvestmentWireTransferField.id]: string | undefined
  [InvestmentWireTransferField.from]: TransferListOption
  [InvestmentWireTransferField.amount]: string | undefined
  [InvestmentWireTransferField.recipientInfo]: string
}

export interface InvestmentRecipientInfoFormInterface {
  [InvestmentWireTransferField.beneficiaryRoutingNumber]?: string
  [InvestmentWireTransferField.beneficiaryAccountNumber]?: string
  [InvestmentWireTransferField.beneficiaryVerifyAccountNumber]?: string
  [InvestmentWireTransferField.beneficiaryAccountTitle]?: string
  [InvestmentWireTransferField.line1]?: string
  [InvestmentWireTransferField.city]?: string
  [InvestmentWireTransferField.region]?: string
  [InvestmentWireTransferField.postalCode]?: string
  [InvestmentWireTransferField.country]: string
}

export enum InputMode {
  Email = 'email',
  Search = 'search',
  Text = 'text',
  Tel = 'tel',
  Url = 'url',
  None = 'none',
  Numeric = 'numeric',
  Decimal = 'decimal',
}

export enum RoutingNumberType {
  ACH_INSTITUTION = 'ACH_INSTITUTION',
  WIRE_INSTITUTION = 'WIRE_INSTITUTION',
}

export enum InputType {
  text = 'text',
  password = 'password',
  email = 'email',
  checkbox = 'checkbox',
  numeric = 'number',
  textarea = 'textarea',
}

export enum EnrollmentTagsEnum {
  WAIVE_MIN_DEPOSIT = 'waive-minimum-deposit',
}

export interface TransferFormInterface {
  amount?: string | undefined
  to: TransferListOption
  from: TransferListOption
}

export enum AccountTransferField {
  To = 'to',
}

export interface AccountTransferForm {
  [AccountTransferField.To]: string
}
